"use strict"

import React from "react"
import { FormattedMessage, injectIntl as inject_intl, useIntl } from "react-intl"
import moment from "moment/moment"

import { parseDate } from "../shared_components/utils"
import ToggleTimeExtractor from "../shared_components/graphing/plotting/widgets/ToggleTimeExtractor"
import ToggleAxisAlignment from "../shared_components/graphing/plotting/widgets/ToggleAxisAlignment"
import { PRESET_METRICS, REPORT_GEN_METRICS } from "../shared_components/graph_dashboards/LTPMDash/utils"
import PriorityIcon from "../shared_components/graphing/plotting/widgets/PriorityIcon"

import { LocaleFormattedDate, LocaleFormattedUser } from "./internationalization/LocaleFormatted"
import { makePlotList as plotListMethod } from "./graphing/resources/PlotList"
import { getObjectsList, loadObservationSeries, loadRunDetails } from "../Loaders"
import { logToServer } from "../ErrorBoundary"
import { withAppSettingsConfig } from "../config/AppSettingsConfigProvider"
import AlterCommentButtons from "./operatoraction/AlterCommentButtons"

const DummyComponent = () => <></> // Empty component
export const isLTPM = true,
  id_accessor = "id",
  getDate = (date, _ = null, string_only = false) =>
    string_only ? moment(date).format("YYYY-MM-DD HH:mm") : <LocaleFormattedDate date={date} />,
  makePlotList = (specialPlots = [], queryTags = [], graphs = [], observationTypeSets = []) =>
    plotListMethod(specialPlots, queryTags, graphs, ...observationTypeSets),
  OperatorInfo = ({ op }) => <LocaleFormattedUser user={op} />,
  OperatorDisplay = ({ op, intl, id }) => {
    if (!op) return <></>

    return (
      <>
        <label style={{ margin: "0" }}>{intl.formatMessage({ id: id })}:</label>
        &nbsp;
        <OperatorInfo op={op} />
      </>
    )
  }

export const RunCommentsIcons = ({ obj, currentReactor, intl }) => {
  return (
    <span style={{ display: "inline-flex", flexDirection: "column" }}>
      <PriorityIcon priority={obj.priority} intl={intl} extraStyle={{ paddingTop: "5px" }} />
      {obj && obj.run_is_live && <AlterCommentButtons obj={obj} reactor={currentReactor} />}
    </span>
  )
}

const single_multi_shared = (multi) => {
  return {
    makePlotList: makePlotList,
    choose_graph_label: (intl) =>
      intl.formatMessage({
        id: "graphing.choose.graph",
      }),
    getExportBaseName: (runData, _, __) => runData.name || "",
    RunFilesDownloader: ({ run_id, fileList, exportBaseName }) => (
      <RunFilesDownloader run_id={run_id} fileList={fileList} exportBaseName={exportBaseName} />
    ),
    SettingsButtons: ({ intl, allowScaleToggle, allowAlignToggle }) => (
      <>
        {multi ? (
          <>
            <ToggleTimeExtractor
              intl={intl}
              toggleAlignment={false}
              showLabel={true}
              dropToggleClass={"width-100"}
            />
            <ToggleAxisAlignment intl={intl} showLabel={true} dropToggleClass={"width-100"} />
          </>
        ) : (
          <ToggleTimeExtractor intl={intl} toggleAlignment={true} dropToggleClass={"width-100"} />
        )}
      </>
    ),
  }
}

export const ltpm_run_navigator = {
  graph_sect_head: (site, RightMenu) => (
    <div style={{ display: "flex", justifyContent: "end" }}>{RightMenu}</div>
  ),
  makePlotList: makePlotList,
}

export const single_run_view = {
  SkynaughtAnnotationsMenu: DummyComponent,
  ...single_multi_shared(false),
}

export const multi_run_view = {
  get_updated_time_label: (run, intl) =>
    `${intl.formatMessage({
      id: "livedash.data.last.loaded",
    })}: ${moment().format("YYYY-MM-DD HH:mm")}`,
  toolbar_right_px: "12px",
  ...single_multi_shared(true),
}

const CrossHairSpan = ({ id, label, val }) => {
  return (
    <>
      {" ; "}
      <FormattedMessage id={id} description={`${label} Tooltip Label`} />
      {" : "}
      <span id={`pointdata_${label}`}>{val}</span>
    </>
  )
}

export const cross_hair_layer = {
  label_supplement: DummyComponent,
  time_info: (timeExtractor, p) => (
    <span id={"pointdata_time"}>
      <FormattedMessage id={"general.time"} description={"Time Tooltip Label"} />
      {` : ${
        timeExtractor.name === "Timestamp"
          ? timeExtractor.verboseFormatter(timeExtractor.getObservationTime(p))
          : timeExtractor.formatter(timeExtractor.getObservationTime(p))
      }`}
    </span>
  ),
  data_info: (p, displayValue) => (
    <>
      {p.value !== undefined && (
        <>
          <CrossHairSpan
            id={"general.value"}
            label={"value"}
            val={p.note || displayValue(p.value) + " [" + p.units + "]"}
          />
          <CrossHairSpan id={"graphing.y.axis"} label={"yaxis"} val={p.axis2 ? "Y2" : "Y1"} />
          <CrossHairSpan id={"general.source"} label={"source"} val={p.source} />
          {p.sample_id && (
            <>
              <CrossHairSpan id={"samplemanagement.sample.id"} label={"sample_id"} val={p.sample_id} />
              {p.alias && <>{` (${p.alias})`}</>}
            </>
          )}
          {p.sample_type && (
            <CrossHairSpan
              id={"samplemanagement.sample.source.type"}
              label={"sample_type"}
              val={p.sample_type}
            />
          )}
        </>
      )}
    </>
  ),
}

const OperatorLabel = ({ id, oa }) => (
  <span>
    [
    <label>
      <FormattedMessage id={id} description={"Operator Label"} />:
    </label>
    <span>
      <LocaleFormattedUser user={oa} />
    </span>
    ]
  </span>
)

export const action_detail_overlay = {
  get_time_and_operator: (
    ev,
    timeExtractor,
    annotation, // annotation not used on LTPM version of component
  ) => (
    <>
      {timeExtractor.name === "Day" ? (
        <>
          <FormattedMessage id={"graphing.timelabel.day"} description={"Day Label"} />
          &nbsp;
          {timeExtractor.formatter(timeExtractor.getActionTime(ev))}
        </>
      ) : (
        timeExtractor.verboseFormatter(timeExtractor.getActionTime(ev))
      )}
      &nbsp;
      {ev.operator?.username && <OperatorLabel id={"general.operator"} oa={ev.operator || {}} />}
      {ev.actor?.username && (
        <>
          , <OperatorLabel id={"general.actor"} oa={ev.actor || {}} />
        </>
      )}
    </>
  ),
}

class AbstractRunConfigClass extends React.Component {
  constructor(props) {
    super(props)

    this.requestActions = this.requestActions.bind(this)
  }

  getApplicationSpecificRegistration(runID, data) {
    return {
      requestActions: this.requestActions.bind(this, runID),
    }
  }

  requestActions(runID) {
    let url = `/warehouse/runs/${runID}/actions/`
    if (this.props.live && this.props.live_run_cutoff) {
      url = url + `?cutoff=${this.props.live_run_cutoff}`
    }
    getObjectsList(url).then((new_actions) => {
      this.setState((state) => {
        const runData = state.runData[runID]
        if (!runData) {
          console.error(`Returned actions for unloaded runID: ${runID}`)
          return null
        }
        return {
          runData: {
            ...state.runData,
            [runID]: {
              ...runData,
              actions:
                new_actions.actions &&
                new_actions.actions.map((action) => ({
                  ...action,
                  timestamp: action.timestamp && parseDate(action.timestamp),
                })),
            },
          },
        }
      })
    })
  }
}

export const run_config_wrapper = {
  loadRunDetails: loadRunDetails,
  loadRunFileList: () => {},
  loadRunMicrobialCommunityData: () => {},
  loadObservationSeries: (_, __) => loadObservationSeries, // Passed argument(s) not used in LTPM
  logToServer,
  AbstractRunConfigClass: AbstractRunConfigClass,
}

// LanzaLake Concepts - Placeholders for LTPM
const DummyConfigWrapper = (
  Component, // No extra config/context passed for LTPM
) =>
  function (props) {
    return <Component {...props} />
  }
export const TimezoneDetails = DummyComponent,
  EmailResultsInputElement = DummyComponent,
  withTimeSeriesClassificationConfig = DummyConfigWrapper,
  withRunSearchState = DummyConfigWrapper,
  withRunEventLabelState = DummyConfigWrapper,
  LegendTimeseriesClassification = DummyComponent,
  RunMatchSelectionLayer = DummyComponent,
  GraphSVGLayers = DummyComponent,
  REACTOR_COLOR_ASSIGNMENTS = {},
  FileDownloadStatus = DummyComponent,
  withApplicationConfig = withAppSettingsConfig,
  RunFilesDownloader = DummyComponent
// --- / LanzaLake Concepts

// LTPM Concepts
// Sometimes intl isn't available to easily pass to translate strings, so we use the
// <FormattedMessage /> component or useIntl() from react-intl
export const getFormattedMessage = (id, msg, getText = false, intl = null, values = {}) => {
  if (id) {
    let translationDetails = { id: id, defaultMessage: msg }
    if (getText) {
      // For the display of text rather than rendering of a component (e.g. 'title')
      return (intl || useIntl()).formatMessage(translationDetails, values)
    } else {
      translationDetails["values"] = values
      return <FormattedMessage {...translationDetails} />
    }
  } else {
    // If id missing for some reason (it happens) fallback to displaying the defaultMessage
    return msg
  }
}

export const injectIntl = (child) => inject_intl(child)

// For shared GraphConfigProvider
export const withAppSettingConfig = withAppSettingsConfig
export const onGraphConfigMount = (context) => {}
export const onGraphConfigUpdate = (context, prevProps) => {
  if (prevProps?.config?.site !== context.props?.config?.site) {
    context.props?.config?.site &&
      getObjectsList("/warehouse/tags/active/")
        .then((querytags) => {
          const relevant_tags = querytags.map((tag) => tag.name)
          const getRelevantPlots = (graphs) => {
            /*
             * Utility function to filter out plots/tags that contain tag references that don't apply to the site at hand.
             * The passed graphs list is shared across many commercial sites so some unused tags might be listed
             * */
            return graphs
              .map((graph) => {
                const plot = Object.assign({}, graph)
                const observations = (plot.observations || []).filter((obv) =>
                  relevant_tags.includes(obv.tag),
                )
                const tags = (plot.tags || []).filter((obv) => relevant_tags.includes(obv.tag))
                if (
                  observations.length > 0 ||
                  plot.title === "Custom comparison" ||
                  (plot.special === "multi" && tags.length > 0)
                ) {
                  plot.observations = observations
                  return plot
                }
              })
              .filter((graph) => graph)
          }

          // Grab the default plots for graphing pages
          getObjectsList(`/users/graph-config/`)
            .then((data) => {
              const parsedData = {}
              const defaultGraphs = {}
              let curated_by = null
              let page_graphs = null
              Object.entries(data).map(([key, value], i) => {
                const parsedGraphs = getRelevantPlots(context.parseGraphs(value["config"]))
                parsedData[key] = parsedGraphs
                if (key === context.props.graphInterface) {
                  curated_by = value["curated_by"]
                  page_graphs = parsedGraphs
                }
                if (!["Performance Analysis", "Comparisons Presets"].includes(key)) {
                  defaultGraphs[key] = {
                    name: key,
                    graphConfigs: parsedGraphs,
                    curated_by: value["curated_by"],
                    isDefault: key === context.props.graphInterface,
                  }
                }
              })
              if (!page_graphs) {
                page_graphs = parsedData["Performance Analysis"]
              }
              const is_system_page = (context.props?.graphInterface || "").includes("System")
              const site = context?.props?.config?.site

              // Get Site or System Specific Dashboard Graphs and Panel Metrics
              const siteKey = is_system_page ? "System" : site
              let panel_metrics = PRESET_METRICS?.[siteKey] || PRESET_METRICS?.["*"] || []
              let report_metrics = REPORT_GEN_METRICS?.[siteKey]
              if (is_system_page) {
                // Step into metrics further
                panel_metrics = panel_metrics?.[site] || panel_metrics?.["*"] || []
                if (report_metrics) {
                  report_metrics = report_metrics?.[site]
                }
              }

              //Finalize Preset Panel Metrics
              const filter_metrics = panel_metrics.filter((obv) => relevant_tags.includes(obv.tag))
              const finalMetrics = filter_metrics.length === 0 ? [panel_metrics[0]] : filter_metrics
              const initialConfigs = {
                live_graphs: page_graphs,
                graphs: [parsedData["Performance Analysis"][0]], // Only want to load a single graph by default here
              }
              context.setState({
                plots_curated_by: curated_by || "LTPM Admin",
                performance_metrics_panel: finalMetrics,
                report_metrics: report_metrics || finalMetrics,
                queryTags: querytags,
                ...initialConfigs,
                special_graphs: page_graphs.concat(parsedData["Comparisons Presets"]),
                initialConfigs: initialConfigs,
                default_graph_configs: Object.values(defaultGraphs),
              })
            })
            .catch((error) => {
              console.error(error)
              logToServer(error)
            })
        })
        .catch((err) => {
          console.error(err)
          logToServer(err)
          context.setState({
            queryTagsErr: err,
          })
        })
  }
}
// --- / LTPM Concepts
